import React, { useEffect, useMemo, useRef } from 'react';
import { Chart, registerables } from 'chart.js';

/** Subset of Chart.js tooltip model used by the external HTML tooltip (avoids brittle type re-exports). */
interface LineTooltipModel {
  opacity: number;
  x: number;
  y: number;
  title: string[];
  body: { lines: string[] }[];
  afterBody: string[];
  labelColors: { backgroundColor: unknown; borderColor: unknown }[];
}

export interface ProgressChartPoint {
  start: string;
  end: string;
  label: string;
  avg: number | null;
  line_segment: string;
  /** Session counts in this date bucket, by activity type (from insights API). */
  session_counts_by_type?: Record<string, number> | null;
}

export interface TrendChartProps {
  /** From AI insights `progress_chart.points` (or legacy-normalized rows). */
  points?: ProgressChartPoint[] | null;
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

function parseISODateHead(iso: string): { y: number; m: number; d: number; mon: string } | null {
  const head = iso.trim().slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(head);
  if (!match) return null;
  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return { y, m: mo, d, mon: MONTH_ABBR[mo - 1] };
}

function monthKey(y: number, m: number): string {
  return `${y}-${m}`;
}

/** X-axis labels: day range only when month matches the previous bucket; repeat month when it changes. */
function buildCompactProgressLabels(points: ProgressChartPoint[]): string[] {
  const out: string[] = [];
  let prevMonthKey: string | null = null;

  for (const p of points) {
    const a = parseISODateHead(p.start ?? '');
    const b = parseISODateHead(p.end ?? '');
    if (!a || !b) {
      out.push(p.label);
      prevMonthKey = null;
      continue;
    }

    if (a.y === b.y && a.m === b.m) {
      const key = monthKey(a.y, a.m);
      const showMonth = prevMonthKey !== key;
      out.push(showMonth ? `${a.d}–${b.d} ${a.mon}` : `${a.d}–${b.d}`);
      prevMonthKey = key;
    } else if (a.y !== b.y) {
      out.push(`${a.d} ${a.mon} ${a.y} – ${b.d} ${b.mon} ${b.y}`);
      prevMonthKey = monthKey(b.y, b.m);
    } else {
      out.push(`${a.d} ${a.mon} – ${b.d} ${b.mon}`);
      prevMonthKey = monthKey(b.y, b.m);
    }
  }

  return out;
}

function tooltipTitleForPoint(p: ProgressChartPoint): string {
  const a = parseISODateHead(p.start ?? '');
  const b = parseISODateHead(p.end ?? '');
  if (!a || !b) return p.label;
  if (a.y === b.y && a.m === b.m) {
    return `${a.d}–${b.d} ${a.mon} ${a.y}`;
  }
  if (a.y !== b.y) {
    return `${a.d} ${a.mon} ${a.y} – ${b.d} ${b.mon} ${b.y}`;
  }
  return `${a.d} ${a.mon} – ${b.d} ${b.mon} ${a.y}`;
}

const SESSION_TYPE_ORDER = ['Interview', 'GD', 'Coding'] as const;

function tooltipSessionCountLines(p: ProgressChartPoint): string[] {
  const raw = p.session_counts_by_type;
  if (!raw || typeof raw !== 'object') return [];

  const lines: string[] = [];
  const seen = new Set<string>();

  for (const key of SESSION_TYPE_ORDER) {
    const n = raw[key];
    if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
      lines.push(` ${key}: ${n}`);
      seen.add(key);
    }
  }

  for (const [key, n] of Object.entries(raw)) {
    if (seen.has(key)) continue;
    if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
      lines.push(` ${key}: ${n}`);
    }
  }

  if (lines.length === 0) return [];
  return ['', ...lines];
}

function renderFrostedTooltipContent(host: HTMLElement, tooltip: LineTooltipModel): void {
  host.replaceChildren();

  for (const line of tooltip.title) {
    const row = document.createElement('div');
    row.style.cssText =
      'color:#A78BFA;font-weight:500;margin-bottom:8px;font-size:13px;line-height:1.35;letter-spacing:0.01em';
    row.textContent = line;
    host.appendChild(row);
  }

  tooltip.body.forEach((bodyItem, i) => {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:8px;margin-bottom:2px';
    const swatch = document.createElement('span');
    const c = tooltip.labelColors[i];
    const bg = c?.backgroundColor ?? '#8B5CF6';
    const bd = c?.borderColor ?? '#8B5CF6';
    swatch.style.cssText = `flex-shrink:0;width:10px;height:10px;border-radius:2px;background:${String(bg)};border:1px solid ${String(bd)}`;
    row.appendChild(swatch);
    const text = document.createElement('span');
    text.style.cssText = 'color:#e6eef8;font-size:12px;line-height:1.4';
    text.textContent = bodyItem.lines.join(' ');
    row.appendChild(text);
    host.appendChild(row);
  });

  const afterLines = tooltip.afterBody.map((l) => l.trim()).filter(Boolean);
  if (afterLines.length > 0) {
    const block = document.createElement('div');
    block.style.cssText =
      'margin-top:8px;padding-top:8px;border-top:1px solid rgba(255,255,255,0.14)';
    afterLines.forEach((line) => {
      const d = document.createElement('div');
      d.style.cssText = 'color:rgba(230,238,248,0.92);font-size:11px;line-height:1.45;margin-top:2px';
      d.textContent = line;
      block.appendChild(d);
    });
    host.appendChild(block);
  }
}

/** Fixed score scale: only these grid lines / labels (10–100). */
const SCORE_Y_AXIS_MIN = 10;
const SCORE_Y_AXIS_MAX = 100;
const SCORE_Y_TICK_VALUES = [10, 20, 40, 60, 80, 100] as const;

const FROSTED_TOOLTIP_CSS: React.CSSProperties = {
  position: 'absolute',
  pointerEvents: 'none',
  opacity: 0,
  zIndex: 20,
  minWidth: 100,
  maxWidth: 280,
  padding: '10px 14px',
  borderRadius: 10,
  background: 'rgba(18, 22, 30, 0.38)',
  backdropFilter: 'blur(16px) saturate(1.35)',
  WebkitBackdropFilter: 'blur(16px) saturate(1.35)',
  border: '1px solid rgba(255, 255, 255, 0.14)',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
  transition: 'opacity 0.1s ease',
};

const TrendChart: React.FC<TrendChartProps> = ({ points }) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<InstanceType<typeof Chart> | null>(null);

  const { pointList, labels, data } = useMemo(() => {
    const list = points?.length ? points : [];
    const lbls = buildCompactProgressLabels(list);
    const vals = list.map((p) => p.avg) as (number | null)[];
    return {
      pointList: list,
      labels: lbls,
      data: vals,
    };
  }, [points]);

  const config = useMemo(() => {
    return {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Average score',
            data,
            fill: false,
            borderColor: '#8B5CF6',
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointBackgroundColor: '#8B5CF6',
            tension: 0,
            spanGaps: false,
            segment: {
              borderDash: (ctx: { p1DataIndex: number }) => {
                const i = ctx.p1DataIndex;
                if (i >= 0 && i < pointList.length && pointList[i]?.line_segment === 'next_peek') {
                  return [5, 5];
                }
                return undefined;
              },
            },
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { left: 4, right: 4, top: 4, bottom: 2 },
        },
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (context: { chart: InstanceType<typeof Chart>; tooltip: LineTooltipModel }) => {
              const el = tooltipRef.current;
              const canvas = context.chart?.canvas;
              const tooltipModel = context.tooltip;
              if (!el || !canvas) return;

              if (tooltipModel.opacity === 0) {
                el.style.opacity = '0';
                return;
              }

              renderFrostedTooltipContent(el, tooltipModel);

              el.style.opacity = '1';
              el.style.left = `${canvas.offsetLeft + tooltipModel.x}px`;
              el.style.top = `${canvas.offsetTop + tooltipModel.y}px`;
            },
            displayColors: true,
            callbacks: {
              title: (items: { dataIndex: number }[]) => {
                const idx = items[0]?.dataIndex;
                if (idx == null || idx < 0 || idx >= pointList.length) return '';
                return tooltipTitleForPoint(pointList[idx]);
              },
              label: (item: { dataIndex: number; parsed: { y: number | null } }) => {
                const idx = item.dataIndex;
                const row = pointList[idx];
                const raw = item.parsed.y;
                const val =
                  raw != null && Number.isFinite(raw)
                    ? Number(raw).toFixed(1)
                    : '—';
                const suffix =
                  row?.line_segment === 'next_peek' ? ' (upcoming window)' : '';
                return ` ${val}${suffix}`;
              },
              afterBody: (tooltipItems: { dataIndex: number }[]) => {
                const idx = tooltipItems[0]?.dataIndex;
                if (idx == null || idx < 0 || idx >= pointList.length) return [];
                return tooltipSessionCountLines(pointList[idx]);
              },
            },
          },
        },
        scales: {
          x: {
            offset: labels.length <= 1,
            grid: {
              display: false,
            },
            ticks: {
              color: '#666',
              font: { size: 11 },
              padding: 4,
              maxRotation: 45,
              minRotation: 0,
            },
            border: { display: false },
          },
          y: {
            min: SCORE_Y_AXIS_MIN,
            max: SCORE_Y_AXIS_MAX,
            afterBuildTicks: (scale: { ticks: { value: number }[] }) => {
              scale.ticks = SCORE_Y_TICK_VALUES.map((value) => ({ value }));
            },
            grid: {
              display: true,
              color: '#333',
              lineWidth: 1,
              drawTicks: false,
            },
            ticks: {
              color: '#666',
              font: { size: 11 },
              padding: 4,
            },
            border: { display: false, dash: [3, 3] },
          },
        },
      },
    };
  }, [pointList, labels, data]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const tooltipEl = tooltipRef.current;

    Chart.register(...registerables);

    chartRef.current?.destroy();
    chartRef.current = new Chart(ctx, config);

    return () => {
      if (tooltipEl) tooltipEl.style.opacity = '0';
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [config]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: 220 }}>
      <canvas ref={canvasRef} />
      <div ref={tooltipRef} style={FROSTED_TOOLTIP_CSS} aria-hidden />
    </div>
  );
};

export default TrendChart;


